/**
 * Manual Refresh Installed Versions — wait for a *new* device-confirmed
 * inventory observation. Publishing refresh_software_inventory alone is never
 * success; Fleet observedAt must advance past the pre-click baseline.
 */

import {
  resolveInventoryObservedAt,
  type SoftwarePackageStatusInput,
} from "./softwareInventoryModel";

export const SOFTWARE_INVENTORY_REFRESH_POLL_MS = 2_000;
/** Bound wait for device PackageManager → Fleet after refresh command. */
export const SOFTWARE_INVENTORY_REFRESH_TIMEOUT_MS = 90_000;

export type SoftwareInventoryRefreshOutcome =
  | "confirmed"
  | "timeout"
  | "offline"
  | "publish_failed"
  | "cancelled";

export const SOFTWARE_INVENTORY_REFRESH_COPY = {
  confirmed: "Installed versions verified from device",
  timeout: "Device did not report fresh inventory in time — last known versions kept",
  offline: "Device appears offline — fresh verification unavailable",
  publish_failed: "Could not request inventory refresh from device",
  cancelled: "Refresh cancelled",
} as const;

export function parseInventoryObservedAtMs(
  value: string | null | undefined
): number | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/** True when next observation is strictly newer than the baseline (device evidence). */
export function isNewerInventoryObservation(args: {
  baselineObservedAt: string | null | undefined;
  nextObservedAt: string | null | undefined;
}): boolean {
  const nextMs = parseInventoryObservedAtMs(args.nextObservedAt);
  if (nextMs == null) return false;
  const baselineMs = parseInventoryObservedAtMs(args.baselineObservedAt);
  if (baselineMs == null) return true;
  return nextMs > baselineMs;
}

/** Newest observedAt across Player / Assistant / Screensaver package rows. */
export function newestInventoryObservedAt(
  statuses: Array<SoftwarePackageStatusInput | null | undefined>
): string | null {
  let best: string | null = null;
  let bestMs = -1;
  for (const status of statuses) {
    const at = resolveInventoryObservedAt(status);
    const ms = parseInventoryObservedAtMs(at);
    if (ms != null && ms > bestMs) {
      bestMs = ms;
      best = at;
    }
  }
  return best;
}

/**
 * Poll package GETs until observedAt advances past baseline, or timeout.
 * Does not invent installed versions — only consumes Fleet package responses.
 */
export async function awaitFreshSoftwareInventoryObservation(args: {
  baselineObservedAt: string | null;
  fetchStatuses: () => Promise<
    Array<SoftwarePackageStatusInput | null | undefined>
  >;
  timeoutMs?: number;
  pollMs?: number;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  isCancelled?: () => boolean;
}): Promise<"confirmed" | "timeout" | "cancelled"> {
  const now = args.now ?? Date.now;
  const sleep =
    args.sleep ?? ((ms: number) => new Promise((r) => setTimeout(r, ms)));
  const timeoutMs = args.timeoutMs ?? SOFTWARE_INVENTORY_REFRESH_TIMEOUT_MS;
  const pollMs = args.pollMs ?? SOFTWARE_INVENTORY_REFRESH_POLL_MS;
  const deadline = now() + timeoutMs;

  while (now() <= deadline) {
    if (args.isCancelled?.()) return "cancelled";
    const statuses = await args.fetchStatuses();
    const newest = newestInventoryObservedAt(statuses);
    if (
      isNewerInventoryObservation({
        baselineObservedAt: args.baselineObservedAt,
        nextObservedAt: newest,
      })
    ) {
      return "confirmed";
    }
    if (now() + pollMs > deadline) break;
    await sleep(pollMs);
  }
  if (args.isCancelled?.()) return "cancelled";
  return "timeout";
}

export function resolveSoftwareInventoryRefreshOutcome(args: {
  deviceAppearsOffline?: boolean;
  publishOk: boolean;
  waitResult?: "confirmed" | "timeout" | "cancelled";
}): SoftwareInventoryRefreshOutcome {
  if (args.deviceAppearsOffline) return "offline";
  if (!args.publishOk) return "publish_failed";
  if (args.waitResult === "confirmed") return "confirmed";
  if (args.waitResult === "cancelled") return "cancelled";
  return "timeout";
}

/**
 * Full manual Refresh flow: publish refresh_software_inventory, then wait for
 * a newer Fleet observedAt. Never treats HTTP accept alone as success.
 */
export async function runManualSoftwareInventoryRefresh(args: {
  deviceAppearsOffline?: boolean;
  baselineObservedAt: string | null;
  publish: () => Promise<unknown>;
  fetchStatuses: () => Promise<
    Array<SoftwarePackageStatusInput | null | undefined>
  >;
  isCancelled?: () => boolean;
  timeoutMs?: number;
  pollMs?: number;
}): Promise<SoftwareInventoryRefreshOutcome> {
  if (args.deviceAppearsOffline) return "offline";
  try {
    await args.publish();
  } catch {
    return "publish_failed";
  }
  const waitResult = await awaitFreshSoftwareInventoryObservation({
    baselineObservedAt: args.baselineObservedAt,
    fetchStatuses: args.fetchStatuses,
    isCancelled: args.isCancelled,
    timeoutMs: args.timeoutMs,
    pollMs: args.pollMs,
  });
  return resolveSoftwareInventoryRefreshOutcome({
    publishOk: true,
    waitResult,
  });
}
